import axios from 'axios'
import { cca_config } from '@/plugins/cca-config'
import router from '@/router'
import store from '@/store'

// Jetons d'accès réutilisables (tableaux, soins, constantes, laboratoire, imagerie) : un seul appel au serveur, partagé
// par tous les écrans tant que le jeton reste valide, au lieu d'un appel avant chaque requête (05/10/2026).
// Les jetons d'action (clôture, séjour, paiement, trésorerie, portail, réattribution…) ne sont jamais réutilisés.
const ACCES_PARTAGES = ['/soins/access', '/medecin/tableau-access', '/infirmier/tableau-access', '/medecin/constantes-access',
    '/infirmier/constantes-access', '/prelevements/access', '/laboratoire/access', '/imagerie/access', '/admin/medical-dashboard-access']
// Gardé 20 s de moins que sa durée annoncée : le jeton n'arrive jamais expiré au serveur.
const MARGE_SECONDES = 20
const accesPartages = {}

const corpsVide = data => data == null || (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
// Chaque écran reçoit sa copie de la réponse : aucun ne peut modifier celle des autres.
const copie = reponse => Object.assign({}, reponse, { data: JSON.parse(JSON.stringify(reponse.data)) })

function accesPartage(post, url, config, token) {
    const role = store.getters['activeRole']
    // Même session, même rôle actif et mêmes options de traitement des erreurs (…_scoped) : même jeton.
    const portee = Object.keys(config || {}).filter(k => /_scoped$/.test(k) && config[k] === true).sort().join(',')
    const cle = [url, token, role && role.id != null ? role.id : '', portee].join('|')
    const maintenant = Date.now()
    Object.keys(accesPartages).forEach(k => { if (accesPartages[k].jusqua <= maintenant) delete accesPartages[k] })
    if (!accesPartages[cle]) {
        const entree = { jusqua: Infinity }   // en cours : partagé avec les appels simultanés
        entree.promesse = post(url, {}, config).then(reponse => {
            const corps = reponse && reponse.data
            const duree = corps && corps.success === true && corps.data && typeof corps.data.access_token === 'string'
                ? Number(corps.data.expires_in) - MARGE_SECONDES : 0
            if (accesPartages[cle] === entree) {
                if (duree > 0) entree.jusqua = Date.now() + duree * 1000
                else delete accesPartages[cle]
            }
            return reponse
        }, erreur => {
            if (accesPartages[cle] === entree) delete accesPartages[cle]
            throw erreur
        })
        accesPartages[cle] = entree
    }
    return accesPartages[cle].promesse.then(copie)
}

export default () => {
    const auth_info = store.getters['authInfo']
    
    let token = ''
    if (auth_info != null) {
        token = (Object.prototype.hasOwnProperty.call(auth_info, 'access_token'))?auth_info.access_token:''
    }
    
    const instance = axios.create({
        baseURL: cca_config.C_URL+cca_config.C_URL_CONFIG_API,
        withCredentials: false,
        headers: {
            'Accept': "application/json",
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
    })

    // Add a response interceptor
    instance.interceptors.response.use(
        response => response, // Return response if successful
        error => {
            const demande = error.config || {}
            const capacite_constantes = demande.constantes_config_scoped === true
                && demande.method === 'post' && demande.url === '/parametres/constantes-access'
            const capacite_kine = demande.kine_config_scoped === true
                && demande.method === 'post' && demande.url === '/parametres/kine-access'
            const capacite_kine_clinique = demande.kine_clinical_scoped === true
                && demande.method === 'post' && demande.url === '/kine/clinical-access'
            const capacite_dossier = demande.constantes_dossier_scoped === true
                && demande.method === 'post' && demande.url === '/medecin/constantes-access'
            const capacite_prescripteurs = demande.prescripteurs_scoped === true
                && demande.method === 'post' && demande.url === '/prescripteurs/access'
            const capacite_soins = demande.soins_access_scoped === true
                && demande.method === 'post' && demande.url === '/soins/access'
            const capacite_dashboard = demande.admin_dashboard_scoped === true
                && demande.method === 'post' && demande.url === '/admin/medical-dashboard-access'
            const capacite_historique = demande.patient_history_scoped === true
                && demande.method === 'post' && demande.url === '/patients/history-access'
            if (error.response && error.response.status === 401 && !capacite_constantes && !capacite_kine && !capacite_kine_clinique && !capacite_dossier && !capacite_prescripteurs && !capacite_soins && !capacite_dashboard && !capacite_historique) {
                console.warn("Token expired for config. Logging out...");
                localStorage.clear();
                router.push('/login'); // Redirect to login page
            }
            return Promise.reject(error);
        }
    );
    const post = instance.post.bind(instance)
    instance.post = (url, data, config) => token && ACCES_PARTAGES.indexOf(url) >= 0 && corpsVide(data)
        ? accesPartage(post, url, config, token) : post(url, data, config)
    return instance
}
